'use client';

import { countryAreaFromMelissa } from '@/lib/address/countryAreaFromMelissa';
import { isBlockedSearchQuery, shouldExcludeAddress } from '@/lib/address/blockedAddresses';
import type { AddressSuggestion, MelissaAddressData, ParsedAddress } from '@/lib/address/types';
import { devError, devLog } from '@/lib/utils/devLog';
import { useCallback, useEffect, useRef, useState } from 'react';

// Re-export types for consumers
export type { AddressSuggestion, ParsedAddress };

const API_BASE_URL = '/api/address/suggestion';
const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;
const DEFAULT_MAX_RECORDS = 10;

interface UseAddressSearchOptions {
  countryCode?: string;
  maxRecords?: number;
  debounceMs?: number;
}

interface UseAddressSearchResult {
  suggestions: AddressSuggestion[];
  isLoading: boolean;
  error: string | null;
  search: (query: string) => void;
  clear: () => void;
}

/**
 * Parse a Melissa address into our internal format
 */
function parseMelissaAddress(melissa: MelissaAddressData, countryCode: string): ParsedAddress {
  return {
    streetAddress1: melissa.DeliveryAddress || melissa.Address1 || '',
    city: melissa.Locality || '',
    postalCode: melissa.PostalCode || '',
    countryArea: countryAreaFromMelissa(countryCode, melissa),
    country: melissa.CountrySubdivisionCode?.split('-')[0] || countryCode,
  };
}

/**
 * Sort addresses by premise number for better UX
 */
function sortByPremise(addresses: AddressSuggestion[]): AddressSuggestion[] {
  return [...addresses].sort((a, b) => {
    const parseNum = (str: string) => {
      const match = str.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
    };
    const aNum = parseNum(a.raw.Premise || '');
    const bNum = parseNum(b.raw.Premise || '');
    return aNum - bNum;
  });
}

/**
 * Hook for address autocomplete suggestions using Melissa API
 *
 * Features:
 * - Debounced search to reduce API calls
 * - Automatic filtering of blocked addresses
 * - Sorted results by premise number
 * - Loading and error states
 *
 * @example
 * ```tsx
 * const { suggestions, isLoading, search, clear } = useAddressSearch({ countryCode: 'US' });
 *
 * <input onChange={(e) => search(e.target.value)} />
 * {suggestions.map(s => <div key={s.id}>{s.displayText}</div>)}
 * ```
 */
export function useAddressSearch(options: UseAddressSearchOptions = {}): UseAddressSearchResult {
  const {
    countryCode = 'US',
    maxRecords = DEFAULT_MAX_RECORDS,
    debounceMs = DEBOUNCE_MS,
  } = options;

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchSuggestions = useCallback(
    async (query: string) => {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setIsLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          address: query,
          countryCode,
          maxRecords: String(maxRecords),
        });

        const response = await fetch(`${API_BASE_URL}?${params}`, {
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to fetch suggestions');
        }

        const json = await response.json();
        devLog('[useAddressSearch] Raw response:', JSON.stringify(json, null, 2));

        // API returns { success: true, data: { Results: [...] } }
        const melissaData = json.data || json;
        const results: MelissaAddressData[] = (melissaData.Results || []).map(
          (item: { Address: MelissaAddressData }) => item.Address
        );
        devLog('[useAddressSearch] Parsed results count:', results.length);

        // Filter out blocked addresses and map to our format
        const validSuggestions: AddressSuggestion[] = results
          .filter((addr) => !shouldExcludeAddress(addr))
          .map((addr, index) => ({
            id: `${addr.PostalCode}-${addr.Premise}-${index}`,
            displayText: addr.Address,
            address: parseMelissaAddress(addr, countryCode),
            raw: addr,
          }));

        setSuggestions(sortByPremise(validSuggestions));
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Ignore aborted requests
          return;
        }
        devError('Address search error:', err);
        setError(err instanceof Error ? err.message : 'Failed to search addresses');
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [countryCode, maxRecords]
  );

  const search = useCallback(
    (query: string) => {
      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Don't search if query is too short
      if (!query || query.length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        setError(null);
        return;
      }

      if (isBlockedSearchQuery(query)) {
        setSuggestions([]);
        setError(null);
        return;
      }

      // Debounce the search
      debounceTimerRef.current = setTimeout(() => {
        fetchSuggestions(query);
      }, debounceMs);
    },
    [fetchSuggestions, debounceMs]
  );

  const clear = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setSuggestions([]);
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    search,
    clear,
  };
}
